import {Box, Checkbox, HStack, Text} from '@chakra-ui/react';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {PromoIcon} from '@/components/svg/PromoIcon';
import {FILTER_BUTTON_PROPS} from './FilterBarStyles';

interface FilterBarPromoCheckboxProps {
  isChecked: boolean;
  isDisabled: boolean;
  hasPromo: boolean;
  onChange: (checked: boolean) => void;
  // Variante mobile : prend toute la largeur disponible et affiche le compteur à côté du label
  mobile?: boolean;
  count?: number;
  /** Surcharge du flex (ex. partager la rangée avec une recherche expandable). */
  flex?: React.ComponentProps<typeof Box>['flex'];
  /** Mobile : n’affiche que l’icône promo (recherche déployée). */
  iconsOnly?: boolean;
}

// Checkbox "Promotions" rouge utilisée dans la FilterBar (catalogue + cadencier).
// Deux variantes via la prop `mobile` :
//  - desktop : encart compact aligné avec les autres filtres de la barre
//  - mobile  : pleine largeur (flex=1), label centré avec le compteur à côté
export const FilterBarPromoCheckbox: React.FC<FilterBarPromoCheckboxProps> = ({
  isChecked,
  isDisabled,
  hasPromo,
  onChange,
  mobile = false,
  count,
  flex,
  iconsOnly = false,
}) => {
  const {t} = useTranslation();

  if (iconsOnly) {
    return (
      <Box
        as='button'
        type='button'
        disabled={isDisabled}
        aria-label={t('catalogue.filtre.promotions')}
        aria-pressed={isChecked}
        height={FILTER_BUTTON_PROPS.height}
        w={FILTER_BUTTON_PROPS.height}
        minW={FILTER_BUTTON_PROPS.height}
        borderRadius='8px'
        border='2px'
        borderColor={isChecked ? 'red.promo_border' : 'gray.light'}
        bg={isChecked ? 'red.promo_bg' : 'gray.75'}
        flex={flex ?? '0 0 auto'}
        display='flex'
        alignItems='center'
        justifyContent='center'
        opacity={hasPromo ? 1 : 0.5}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        transition='width 0.2s ease, min-width 0.2s ease, background-color 0.15s ease'
        onClick={() => {
          onChange(!isChecked);
        }}
      >
        <PromoIcon color='red.fail' />
      </Box>
    );
  }

  return (
    <Box
      height='38px'
      p='7px 11px'
      m={mobile && flex === undefined ? '0px 5px' : undefined}
      borderRadius='8px'
      fontWeight='500'
      border='2px'
      borderColor={isChecked ? 'red.promo_border' : 'gray.light'}
      bg={isChecked ? 'red.promo_bg' : 'gray.75'}
      flex={flex ?? (mobile ? 1 : undefined)}
      minW={0}
      display='flex'
      alignItems='center'
      justifyContent={mobile ? 'center' : undefined}
      opacity={hasPromo ? 1 : 0.5}
      transition='flex 0.2s ease, padding 0.2s ease'
    >
      <HStack
        spacing={1}
        alignItems='center'
      >
        <Checkbox
          borderColor='gray.light'
          size='sm'
          isChecked={isChecked}
          isDisabled={isDisabled}
          onChange={(e) => {
            onChange(e.target.checked);
          }}
          sx={{
            '& .chakra-checkbox__control': {
              borderColor: 'gray.light',
              _checked: {
                bg: 'red.fail',
                borderColor: 'red.fail',
                _hover: {bg: 'red.fail', borderColor: 'red.fail'},
              },
              _hover: {bg: 'transparent'},
            },
          }}
        >
          <Text
            color='blue.text'
            fontSize='xs'
          >
            {t('catalogue.filtre.promotions')}
            {mobile && count !== undefined ? ` (${count})` : ''}
          </Text>
        </Checkbox>
        <PromoIcon color='red.fail' />
      </HStack>
    </Box>
  );
};
